import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, computed, inject, Inject, OnDestroy, Optional, Signal, signal, ViewChild, WritableSignal, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, FormsModule, NgForm, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialog } from '@angular/material/dialog';
import { MAT_BOTTOM_SHEET_DATA } from '@angular/material/bottom-sheet';
import { ModalService } from '@app/_services/modal.service';
import { MatMenuModule } from '@angular/material/menu';
import { Attachment, TextEditorActiveFormats } from '@app/_interfaces/generic.interface';
import { Habit, HabitNumericAnswer, HabitSetup, HabitTimerAnswer, HabitTimerState } from '@app/_interfaces/habit.interface';
import { AlertService } from '@app/_services/alert.service';
import { CacheService } from '@app/_services/cache.service';
import { DependencyService } from '@app/_services/dependency.service';
import { FirebaseService } from '@app/_services/firebase.service';
import { UtilsService } from '@app/_services/utils.service';
import { AttachmentListComponent } from '@app/components/addons/attachments/attachment-list/attachment-list.component';
import { HashtagComponent } from '@app/components/addons/hashtags/hashtag/hashtag.component';
import { InputCheckmarkAdvancedComponent } from '@app/components/shared/inputs/input-checkmark-advanced/input-checkmark-advanced.component';
import { InputFileComponent } from '@app/components/shared/inputs/input-file/input-file.component';
import { InputHashtagComponent } from '@app/components/shared/inputs/input-hashtag/input-hashtag.component';
import { InputAlertToneSelectComponent } from '@app/components/shared/inputs/input-alert-tone-select/input-alert-tone-select.component';
import { SvgComponent } from '@app/components/shared/svg/svg.component';
import _ from 'lodash';
import { Subject, takeUntil, timer } from 'rxjs';
import { HabitStore } from '@app/_stores';
import { HabitSetupFormComponent } from '../habit-setup-form/habit-setup-form.component';
import { CollaboraterRole, HabitType, TimerStopType } from '@app/_types/generic.type';
import { InputTextEditorComponent } from '@app/components/shared/inputs/input-text-editor/input-text-editor.component';
import { ParseSecondsPipe } from '@app/_pipes/parse-seconds.pipe';
import { HabitNumericValueComponent } from '../habit-numeric-value/habit-numeric-value.component';
import { HabitTimerService } from '@app/_services/habit-timer.service';
import { SettingsService } from '@app/_services/settings.service';
import { AttachmentsService } from '@app/_services/attachments.service';
import { textEditorDefaultActiveFormats } from '@app/_datas/const.data';
import { TextEditorOptionsComponent } from '@app/components/shared/text-editor-options/text-editor-options.component';
import { AnalyticsService } from '@app/_services/analytics.service';
import { MapService } from '@app/_services/map.service';
import { AnalyticsScreen, SubFeatureActionValue } from '@app/_types/analytics-events.types';
import { StorageService } from '@app/_services/storage.servive';
import { FirestoreCollection } from '@app/_enums/firestore-collection.enum';
import { CommentsListComponent } from '@app/components/addons/comments/comments-list/comments-list.component';
import { FeatureService } from '@app/_services/features/features.service';
import { ParseRemainingDaysPipe } from '@app/_pipes/parse-remaining-days.pipe';
import { log } from '@app/_services/logger';
import { toDate } from '@app/_services/date';
import { CustomBottomSheetRef } from '@app/_services/custom-bottom-sheet.service';
import { LastUpdatedByService } from '@app/_services/last-updated-by.service';
import { getRoleByUid } from '@app/_utils/members-model.utils';
import { MenuManagerService } from '@app/_services/menu-manager.service';
import { HABIT_ACTION_MENU } from '@app/_configs/menu-configs/habit-action-menu.config';
import { MenuAction } from '@app/_interfaces/menu-config.interface';
import { TimeFormatPipe } from '@app/_pipes/time-format.pipe';

@Component({
  selector: 'app-habit-form',
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    SvgComponent,
    InputTextEditorComponent,
    InputHashtagComponent,
    AttachmentListComponent,
    InputFileComponent,
    MatMenuModule,
    TextEditorOptionsComponent,
    ParseRemainingDaysPipe,
    TimeFormatPipe
  ],
  templateUrl: './habit-form.component.html',
  styleUrl: './habit-form.component.scss'
})

export class HabitFormComponent implements OnDestroy, OnInit {

  @ViewChild('haForm') haForm!: NgForm;
  @ViewChild(InputTextEditorComponent) textEditor!: InputTextEditorComponent;

  unSubscribe = new Subject<void>();
  habitForm: FormGroup;
  habitInitial: Habit;
  readonly habitStore = inject(HabitStore);
  readonly lastUpdatedByService = inject(LastUpdatedByService);
  readonly menuManager = inject(MenuManagerService);
  newAttachments!: FormGroup;
  deletedAttachments!: FormGroup;

  // Menu configuration
  private readonly menuConfig = HABIT_ACTION_MENU;

  // Computed menu actions for responsive rendering
  topBarActions = computed(() =>
    this.menuManager.getTopBarActions(this.menuConfig, this)
  );

  // Filter out close and kebab from regular rendering (they're handled separately)
  topBarActionsWithoutSpecial = computed(() =>
    this.topBarActions().filter(
      (action) => action.id !== 'close' && action.id !== 'kebab'
    )
  );

  // Get kebab action separately
  kebabAction = computed(() =>
    this.topBarActions().find((action) => action.id === 'kebab')
  );

  kebabMenuActions = computed(() =>
    this.menuManager.getKebabMenuActions(
      this.menuConfig,
      this.topBarActions(),
      this
    )
  );

  showKebabMenu = computed(() =>
    this.menuManager.shouldShowKebab(this.kebabMenuActions())
  );
  public data: { mode: 'new' | 'edit', setupId: string, value: Habit, dateString: string };
  newAttachmentsSignal = computed(() => Object.values(this.newAttachments?.value || {}) as Attachment[]);
  deletedAttachmentsSignal = computed(() => Object.values(this.deletedAttachments?.value || {}) as Attachment[]);

  mode: Signal<'new' | 'edit'> = computed(() => {
    const habitValue = this.habitValue();
    if (habitValue && habitValue?.id) {
      this.habitInitial = this.initiateForm(habitValue);
      return 'edit';
    } else {
      this.habitInitial = this.initiateForm();
      return 'new';
    };
  });
  setup: Signal<HabitSetup> = computed(() => {
    return this.habitStore.idToSetup()[this.data.setupId];
  });
  isDescription: boolean = false;

  isCollab: Signal<boolean> = computed(() => {
    return (this.setup().members?.memberUids?.length || 0) > 0;
  });

  role: Signal<CollaboraterRole> = computed(() => {
    if (this.setup().uid === this.cc.user.uid) {
      return 'owner';
    } else if (this.setup().members) {
      return getRoleByUid(this.setup().members, this.cc.user.uid) || 'viewer';
    } else {
      return 'viewer';
    }
  });

  habitValue: Signal<Habit> = computed(() => {
    return this.habitStore.habitMap().setups[this.data.setupId]?.[this.data.dateString] || {} as Habit;
  });

  habitType: Signal<HabitType> = computed(() => {
    return this.setup().habitType;
  });
  deleted: Signal<boolean> = computed(() => {
    // if (this.mode() === 'new') return false;
    const deletedAt = this.setup().deletedAt;
    if (!deletedAt) return false;
    return true;
  });

  duration: WritableSignal<{ year: number, month: number, day: number, hour: number, minute: number, second: number, totalSeconds: number }> = signal({ year: 0, month: 0, day: 0, hour: 0, minute: 0, second: 0, totalSeconds: 0 });
  timerAnswers: Signal<HabitTimerAnswer[]> = computed(() => {
    return this.habitValue()?.timerAnswer || [];
  });
  numericAnswers: WritableSignal<HabitNumericAnswer[]> = signal<HabitNumericAnswer[]>([]);
  reversedTimerAnswers: Signal<HabitTimerAnswer[]> = computed(() => {
    // CRITICAL FIX: Only show completed timers (endTimestamp in the PAST)
    // Flutter sets endTimestamp to future time (start + goal) for running timers!
    const now = Date.now();
    return this.timerAnswers()
      .filter(answer => {
        if (!answer.endTimestamp) return false; // No endTimestamp = running
        const endTime = new Date(answer.endTimestamp).getTime();
        return endTime <= now; // Only show if end time is in the past
      })
      .slice()
      .reverse();
  });

  reversedNumericAnswers: Signal<HabitNumericAnswer[]> = computed(() => {
    return [...this.numericAnswers()].sort((a, b) => {
      const aDate = this.normalizeDate(a.timestamp);
      const bDate = this.normalizeDate(b.timestamp);

      if (!aDate || !bDate) return 0;

      // Descending → latest time first (includes hours, minutes, seconds, ms)
      return bDate.getTime() - aDate.getTime();
    });
  });

  totalNumericValue = computed(() => {
    let total = 0;

    this.numericAnswers().forEach((answer: HabitNumericAnswer) => {
      total += answer.value;
    });

    return total;
  });
  totalNumericPercentage = computed<number>(() => {
    if (!this.setup().numericGoal) return 0;

    const percentage = (this.totalNumericValue() / (this.setup().numericGoal || 1)) * 100;

    return Math.floor(percentage);
  });

  durationRepeatCount: WritableSignal<number> = signal(5);
  durationRepeatType: Signal<number> = computed(() => {
    return this.setup().durationRepeatType;
  });
  divisionAngles = computed(() => {
    const angleStep = 360 / this.durationRepeatCount();
    return Array.from({ length: this.durationRepeatCount() }, (_, i) => i * angleStep - 180);
  });

  timerSeconds: Signal<number> = computed(() => {
    const timerState = this.timerState();
    this.timerService.timerTick();
    if (!timerState) return 0;
    const now = new Date();
    const elapsedMs = now.getTime() - new Date(timerState.startTimestamp).getTime();
    const elapsedSeconds = Math.floor(elapsedMs / 1000);
    return elapsedSeconds;
  });

  totalSecondsSpent = computed(() => {
    // CRITICAL FIX: Only count completed timers (endTimestamp in the PAST)
    // Flutter sets endTimestamp to future time (start + goal) for running timers!
    const now = Date.now();
    const completedTimers = this.timerAnswers().filter(answer => {
      if (!answer.endTimestamp) return false; // No endTimestamp = running
      const endTime = new Date(answer.endTimestamp).getTime();
      return endTime <= now; // Only count if end time is in the past
    });
    return this.timerService.getTotalSecondsSpent(completedTimers);
  });

  currentRound = computed(() => {
    return this.timerService.getCurrentRound(this.totalSecondsSpent(), this.duration().totalSeconds, this.setup().durationRepeatCount);
  });

  currentRoundSeconds = computed(() => {
    return this.totalSecondsSpent() - (this.duration().totalSeconds * (this.currentRound() - 1));
  });

  totalTimePercentage = computed<number>(() => {
    let spent = this.totalSecondsSpent();
    const startDate = this.timerState() ? this.timerState()?.startTimestamp : null;
    if (startDate) {
      const additionalSeconds = this.timerSeconds(); // your manually tracked seconds
      spent += Math.max(0, additionalSeconds); // ✅ add this instead of ongoing time
    }
    if (this.duration().totalSeconds === 0) {
      if (spent === 0) {
        return 0;
      } else {
        return 100;
      }
    } else {
      const totalSeconds = this.duration().totalSeconds * this.setup().durationRepeatCount;
      if (!totalSeconds) return 0;

      const percentage = (spent / totalSeconds) * 100;

      return Math.floor(percentage); // allow > 100%
    }
  });

  timerState: Signal<HabitTimerState | null> = computed(() => {
    const timers = this.timerService.timers();
    const id = `${this.data.setupId}&&&${this.data.dateString}`;
    return timers[id] || null;
  });

  availableDates: Signal<string[]> = computed(() => {
    const setup = this.setup();
    return this.ds.getAllDatesOfEntity(setup, 'habit', this.cc.user.uid);
  });

  isValidEntry: Signal<boolean> = computed(() => {
    const dateString = this.data.dateString;
    const allDates = this.availableDates();
    return allDates.includes(dateString);
  });

  isAddFab: Signal<boolean> = computed(() => {
    if (this.habitType() === 'numeric') {
      if (this.role() !== 'viewer') {
        return true;
      }
    } else if (this.habitType() === 'timer') {
      if (this.role() !== 'viewer') {
        // CRITICAL: If timer is currently running, always show FAB (even if goal is complete)
        // This handles the undo scenario where goal becomes complete but timer is still running
        const isTimerRunning = this.timerState() !== null;
        if (isTimerRunning) {
          return true;
        }

        const repeatType = this.durationRepeatType();
        const totalRounds = this.durationRepeatCount();
        const totalSecondsSpent = this.totalSecondsSpent();
        const durationSeconds = this.duration().totalSeconds;
        const totalSeconds = durationSeconds * totalRounds;

        // If no goal is set (duration is 0), treat as unlimited mode
        if (durationSeconds === 0) {
          return true;
        }

        if (repeatType === 0) {
          // Unlimited mode - always show FAB
          return true;
        } else if (repeatType === 1) {
          // Goal-based mode - always show FAB (allow exceeding goal)
          return true;
        }
      }
    }
    return false;
  });

  timerStopType: Signal<TimerStopType> = computed(() => {
    return this.setup().timerStopType;
  });

  roundCompleteSecondsLeft: Signal<number> = computed(() => {
    const timerStopType = this.timerStopType();
    if (timerStopType === 'never') return 0;
    const secondsLeft = this.duration().totalSeconds - this.currentRoundSeconds();
    if (secondsLeft === 0) return this.duration().totalSeconds;
    return secondsLeft;
  });

  editorFocused: WritableSignal<boolean> = signal(false);
  dropdownPosition: WritableSignal<{ top: number; left: number }> = signal({ top: 0, left: 0 });
  activeFormats: WritableSignal<TextEditorActiveFormats> = signal(textEditorDefaultActiveFormats);

  screenEnterTime: WritableSignal<Date> = signal(new Date());

  habitTypeString: Signal<SubFeatureActionValue> = computed(() => {
    return this.mapService.subTypesMap()[this.setup().habitType];
  });
  newEntityId: string = this.utilsService.getNewId();

  remindingDays: Signal<number | null> = computed(() => {
    return this.featureService.getRemainingDays(
      this.setup().repeat,
      this.setup().startAt.dateString,
      this.setup().endAt?.dateString || null,
      this.cc.today().dateString
    );
  });

  normalizeDate = (date: any): Date | null => {
    if (!date) return null;
    if (typeof date === 'object' && '_seconds' in date) return toDate(date);
    return new Date(date);
  };

  entryDate: WritableSignal<Date>;
  isHideRemainingDays: WritableSignal<boolean> = signal(false);

  // Track last deleted attachment for undo functionality
  private lastDeletedAttachment: { attach: Attachment; formArray: FormGroup; wasCloud: boolean } | null = null;
  private currentUndoSubscription: any = null;
  private currentUndoTimeout: any = null;
left: any;

  constructor(
    @Optional() public dialogRef: MatDialogRef<HabitFormComponent> | null,
    @Optional() public bottomSheetRef: CustomBottomSheetRef<HabitFormComponent> | null,
    @Optional() @Inject(MAT_DIALOG_DATA) dialogData: { mode: 'new' | 'edit', setupId: string, value: Habit, dateString: string } | null,
    @Optional() @Inject(MAT_BOTTOM_SHEET_DATA) bottomSheetData: { mode: 'new' | 'edit', setupId: string, value: Habit, dateString: string } | null,
    private fb: FormBuilder,
    public cc: CacheService,
    private utilsService: UtilsService,
    public dialog: MatDialog,
    private modalService: ModalService,
    private alertService: AlertService,
    public ds: DependencyService,
    private fbService: FirebaseService,
    private timerService: HabitTimerService,
    public ss: SettingsService,
    private attachmentsService: AttachmentsService,
    private as: AnalyticsService,
    private mapService: MapService,
    private storageService: StorageService,
    private featureService: FeatureService,

    private cdr: ChangeDetectorRef,
  ) {
    this.data = (dialogData || bottomSheetData)!;
    this.entryDate = signal(new Date(this.data.dateString));
    this.newAttachments = this.fb.group({});
    this.deletedAttachments = this.fb.group({});
    // this.mode.set(data.mode);
    this.habitInitial = this.data.mode == 'new' ? this.initiateForm() : this.initiateForm(this.data.value);
    const duration = this.habitType() === 'timer' && this.setup().timerGoal ? this.ds.parseMinutes(this.setup().timerGoal || '') : { year: 0, month: 0, day: 0, hour: 0, minute: 0, second: 0, totalSeconds: 0 };
    this.duration.set(duration);
    this.durationRepeatCount.set(this.setup().durationRepeatCount || 1);

    log.debug('constructor', 'Habit data', { value: this.data.value });
    log.debug('constructor', 'Habit setup', { setup: this.setup() });

    if (this.habitInitial.description) {
      this.isDescription = true;
    }

    this.habitForm = this.fb.group({
      id: new FormControl(this.habitInitial.id, Validators.required),
      description: new FormControl(this.habitInitial.description),
      dueAt: new FormControl(this.habitInitial.dueAt),
      booleanAnswer: new FormControl(this.habitInitial.booleanAnswer),
      numericAnswer: new FormControl(this.habitInitial.numericAnswer),
      timerAnswer: new FormControl(this.habitInitial.timerAnswer),
      singleAnswer: new FormControl(this.habitInitial.singleAnswer),
      multipleAnswer: new FormControl(this.habitInitial.multipleAnswer),
      tags: new FormControl(this.habitInitial.tags),
      attachments: this.fb.group({}),
      setupId: new FormControl(this.habitInitial.setupId),
      uid: new FormControl(this.habitInitial.uid, Validators.required),
      members: new FormControl(this.habitInitial.members),
    });

    if (this.habitInitial.numericAnswer) {
      this.numericAnswers.set(this.habitInitial.numericAnswer);
    }

    const attachmentIds = Object.keys(this.habitInitial.attachments);
    this.clearAttachments();
    attachmentIds.forEach(id => {
      this.attachments.addControl(id, this.ds.addAttachmentForm(this.habitInitial.attachments[id]));
    });

    dialogRef?.backdropClick().pipe(takeUntil(this.unSubscribe)).subscribe(async () => {
      this.closeDialog();
    });
    bottomSheetRef?.backdropClick().pipe(takeUntil(this.unSubscribe)).subscribe(async () => {
      this.closeDialog();
    });
  }

  ngOnInit() {
    const previousScreen = this.storageService.getCurrentScreen() as AnalyticsScreen;
    this.as.screenViewEvent(this.mapService.habitActionTypeAnalyticsMap()[this.habitType()], previousScreen, this.habitTypeString());
    if (this.data.mode === 'edit') {
      this.as.crudEvent('read', this.mapService.habitActionTypeAnalyticsMap()[this.habitType()], this.cc.user.uid === this.setup().uid ? 'private' : 'shared', this.setup().members?.memberUids.length || 0, 'notShared', this.role(), 'none');
    }
  }

  get hashtags(): string[] {
    const setupTags = this.setup().tags;
    const journalTags = this.habitForm.value.tags;
    const tags = [...journalTags, ...setupTags];
    const validHashtags = this.ss.getValidHashtags(tags);
    const uniqueHashtags = [...new Set(validHashtags)];
    return uniqueHashtags;
  }

  startTimer() {
    this.timerService.startTimer(this.data.dateString, this.setup().id, this.duration().totalSeconds);
  }

  stopTimer() {
    this.timerService.stopTimer(`${this.data.setupId}&&&${this.data.dateString}`);
  }

  updateTimeValue() {
    const habitData: Habit = this.mode() === 'new' ? this.habitStore.getNewHabit() : this.data.value;
    const updatedHabit = { ...habitData, timerAnswer: this.timerAnswers() };

    if (this.mode() === 'new') {
      this.habitStore.addHabit(updatedHabit);
    } else if (this.mode() === 'edit') {
      this.habitStore.updateHabits([updatedHabit]);
    }
    this.data.value = updatedHabit;
    this.habitInitial = this.initiateForm(updatedHabit);
  }

  addNumericValue(value?: HabitNumericAnswer) {
    const dialogRef = this.dialog.open(HabitNumericValueComponent, {
      width: '100%',
      maxWidth: '500px',
      disableClose: true,
      data: {
        mode: value ? 'edit' : 'new',
        value: value ? value : null,
        unit: this.setup().numericUnit
      }
    });

    dialogRef.afterClosed().pipe(takeUntil(this.unSubscribe)).subscribe(async (result) => {
      if (result) {
        const numericAnswer = this.habitForm.get('numericAnswer')?.value;
        if (result.type === 'ADD') {
          this.habitForm.get('numericAnswer')?.setValue(numericAnswer ? [...numericAnswer, result.value] : [result.value]);
          this.numericAnswers.set(this.habitForm.value.numericAnswer);
        } else if (result.type === 'EDIT') {
          this.habitForm.get('numericAnswer')?.setValue(numericAnswer.map((v: HabitNumericAnswer) => v.id === result.value.id ? result.value : v));
          this.numericAnswers.set(this.habitForm.value.numericAnswer);
        }
      }
    });
  }

  deleteTimerValue(value: HabitTimerAnswer) {
    const updatedTimerAnswers = this.timerAnswers().length === 1 ? [] : this.timerAnswers().filter((v: HabitTimerAnswer) => v.id !== value.id);
    const habitData = this.habitValue() ? this.habitValue() : this.habitStore.getNewHabit();
    const updatedHabit = { ...(habitData || this.habitStore.getNewHabit()), timerAnswer: updatedTimerAnswers };
    this.habitStore.updateHabits([updatedHabit], true);
  }

  async deleteNumericValue(value: HabitNumericAnswer) {
    const oldNumericAnswer = this.habitForm.value.numericAnswer;
    if (this.habitForm.value.numericAnswer.length === 1) {
      this.habitForm.get('numericAnswer')?.setValue(null);
      this.numericAnswers.set([]);
    } else {
      this.habitForm.get('numericAnswer')?.setValue(this.habitForm.value.numericAnswer.filter((v: HabitNumericAnswer) => v.id !== value.id));
      this.numericAnswers.set(this.habitForm.value.numericAnswer);
    }
    const undo = await this.alertService.toast('habitResponseEntryDeleted_content', 'common_undo', 'common_dismiss');
    if (undo) {
      this.habitForm.get('numericAnswer')?.setValue(oldNumericAnswer);
      this.numericAnswers.set(oldNumericAnswer);
    }
  }

  get attachmentValues(): Attachment[] {
    const toDateSafe = (value: any): Date => {
      if (!value) return new Date(0);

      // Firestore Timestamp object case
      if (typeof value === 'object' && '_seconds' in value) {
        return toDate(value) ?? new Date(0);
      }

      // ISO string / number case
      return new Date(value);
    };

    return ([
      ...Object.values(this.attachments.value),
      ...Object.values(this.newAttachments.value)
    ] as Attachment[])
      .filter(a => a.deletedAt === null)
      .sort((a, b) => toDateSafe(b.createdAt).getTime() - toDateSafe(a.createdAt).getTime());
  }

  get attachments(): FormGroup {
    return this.habitForm.get('attachments') as FormGroup;
  }

  clearAttachments() {
    this.attachments.reset();
  }

  get basePath(): string {
    return `userData/attachments/${this.mode() === 'new' ? this.cc.user.uid : this.setup().uid}/habitActions/${this.habitInitial.id}`;
  }

  initiateForm(habit?: Habit): Habit {
    return {
      id: habit ? habit.id : this.newEntityId,
      description: habit ? habit.description : '',
      dueAt: habit ? habit.dueAt : this.data.dateString ? this.utilsService.getCustomDate(this.data.dateString) : this.utilsService.getCustomDate(),
      booleanAnswer: habit ? habit.booleanAnswer : null,
      timerAnswer: habit ? habit.timerAnswer : null,
      numericAnswer: habit ? habit.numericAnswer : null,
      singleAnswer: habit ? habit.singleAnswer : null,
      multipleAnswer: habit ? habit.multipleAnswer : null,
      tags: habit ? habit.tags : [],
      attachments: habit ? habit.attachments : {},
      setupId: this.data.setupId,
      uid: this.setup().uid,
      members: habit ? habit.members : this.setup().members,
    }
  };

  progressColor: Signal<string> = computed(() => {
    if (this.habitType() === 'numeric') {
      const setup = this.setup();
      if (setup.numericGoal === null) return `conic-gradient(${this.deleted() ? this.ss.theme().color7 : this.ss.theme().color1} ${(this.totalNumericValue() ? 100 : 0) * 3.6}deg, ${this.ss.theme().color10} 0deg)`;
      return `conic-gradient(${this.deleted() ? this.ss.theme().color7 : this.ss.theme().color1} ${(this.totalNumericPercentage()) * 3.6}deg, ${this.ss.theme().color10} 0deg)`;
    } else if (this.habitType() === 'timer') {
      return `conic-gradient(${this.deleted() ? this.ss.theme().color7 : this.ss.theme().color1} ${(this.totalTimePercentage()) * 3.6}deg, ${this.ss.theme().color10} 0deg)`;
    } else {
      return 'none';
    }
  });

  trackByIndex = (index: number) => index;

  getFc(fcName: string): FormControl {
    return this.habitForm.get(fcName) as FormControl;
  }

  selectOption(option: number) {
    if (this.habitType() === 'single') {
      this.habitForm.get('singleAnswer')?.setValue(option);
    } else if (this.habitType() === 'multiple') {
      const current: number[] = this.getFc('multipleAnswer').value || [];
      if (current && current.includes(option)) {
        this.getFc('multipleAnswer').setValue(current.filter((id: number) => id !== option));
      } else {
        const values = [...current, option];
        this.getFc('multipleAnswer').setValue(values);
      }
    }
  }

  hasChanges(): boolean {
    const initial = _.cloneDeep(this.habitInitial);
    const current = _.cloneDeep(this.habitForm.value);

    initial.timerAnswer = [];
    current.timerAnswer = [];
    initial.id = '';
    current.id = '';
    initial.dueAt = null as any;
    current.dueAt = null as any;


    const initialAttachmentLength = Object.keys(initial.attachments).length;
    const currentAttachmentLength = Object.keys(current.attachments).length;
    const newAttachmentsLength = Object.keys(this.newAttachments.value).length;
    const deletedAttachmentsLength = Object.keys(this.deletedAttachments.value).length;
    if (initialAttachmentLength !== currentAttachmentLength || newAttachmentsLength > 0 || deletedAttachmentsLength > 0) return true;

    return !_.isEqual(initial, current);
  }

  hasValue(): boolean {
    return (this.habitForm.value.description.trim() !== '' || this.attachmentValues.length > 0);
  }

  async deleteAttachments(attachments: Attachment[]) {
    attachments?.forEach(attachment => {
      const deletedAttachmentData: { attach: Attachment; formArray: FormGroup; wasCloud: boolean } = {
        attach: attachment,
        formArray: null as any,
        wasCloud: attachment.status === 'cloud'
      };

      const attachmentFormArray = attachment.status === 'local' ? this.newAttachments : this.attachments;
      const formControl = attachmentFormArray.get(attachment.id.toString());

      if (formControl) {
        deletedAttachmentData.formArray = formControl as FormGroup;
      }

      // Remove from form arrays
      attachmentFormArray.removeControl(attachment.id.toString());
      if (attachment.status === 'cloud') {
        this.deletedAttachments.addControl(attachment.id.toString(), this.ds.addAttachmentForm(attachment));
      }
      this.cdr.markForCheck();

      // Clean up previous undo subscription and timeout if exists
      if (this.currentUndoSubscription) {
        this.currentUndoSubscription.unsubscribe();
      }
      if (this.currentUndoTimeout) {
        clearTimeout(this.currentUndoTimeout);
      }

      // Store as the LAST deleted attachment (overwrites any previous)
      this.lastDeletedAttachment = deletedAttachmentData;

      // Wait for undo event - only the LAST deletion can be undone
      this.currentUndoSubscription = this.attachmentsService.undoEvent$
        .pipe(takeUntil(this.unSubscribe))
        .subscribe((shouldUndo: boolean) => {
          if (shouldUndo && this.lastDeletedAttachment?.formArray) {
            // Restore the LAST deleted attachment only
            const targetFormArray = this.lastDeletedAttachment.attach.status === 'local' ? this.newAttachments : this.attachments;
            targetFormArray.addControl(this.lastDeletedAttachment.attach.id.toString(), this.lastDeletedAttachment.formArray);
            if (this.lastDeletedAttachment.wasCloud) {
              this.deletedAttachments.removeControl(this.lastDeletedAttachment.attach.id.toString());
            }
            this.cdr.markForCheck();
            // Clean up
            if (this.currentUndoTimeout) clearTimeout(this.currentUndoTimeout);
            this.currentUndoSubscription.unsubscribe();
            this.lastDeletedAttachment = null;
          }
        });

      // Auto-cleanup subscription after 5 seconds if no undo
      this.currentUndoTimeout = setTimeout(() => {
        if (this.currentUndoSubscription) {
          this.currentUndoSubscription.unsubscribe();
        }
        this.lastDeletedAttachment = null;
      }, 5000);
    });
  }

  async editSetup() {
    await this.closeDialog();

    const data = {
      mode: 'edit',
      value: this.setup(),
      habitType: this.habitType(),

    }

    if (this.cc.isNarrowScreen()) {
      const setupDialog = this.modalService.openMainContent(
        HabitSetupFormComponent,
        data,
        { disableClose: true, autoFocus: false }
      );
    } else {
      const setupDialog = this.modalService.openLevel1(
        HabitSetupFormComponent,
        data,
        { disableClose: true, autoFocus: false }
      );
    }

  }

  async save() {
    this.cc.isLoading.set(true);
    if (!this.habitForm.value.id) return;

    const newAttachments: Attachment[] = Object.values(this.newAttachments.value);
    const deletedAttachments: Attachment[] = Object.values(this.deletedAttachments.value);
    const attachments = await this.attachmentsService.updateAttachments(newAttachments, this.basePath);
    const attachmentIds = Object.keys(attachments.value);
    attachmentIds.forEach(id => {
      this.attachments.addControl(id, attachments.get(id));
    });
    // Clear newAttachments after successful upload to prevent duplicates in attachmentValues getter
    this.newAttachments = this.fb.group({});
    await this.attachmentsService.deleteAttachments(deletedAttachments, this.basePath);

    const habitData: Habit = this.habitForm.value;

    if (this.habitType() === 'timer') {
      habitData.timerAnswer = this.timerAnswers().length > 0 ? this.timerAnswers() : null;
    }

    if (this.mode() === 'new') {
      this.habitStore.addHabit(habitData);
    } else if (this.mode() === 'edit') {
      const updatedHabit = { ...this.habitValue(), ...habitData };
      this.habitStore.updateHabits([updatedHabit]);
    }

    this.cc.isLoading.set(false);
    this.close({ mode: this.mode(), id: habitData.id });
  }

  async resetHabit() {
    if (!this.isFormHasValue()) return;
    const res = await this.alertService.confirm(this.ss.texts()['habitActionReset_title'], this.ss.texts()['common_resetResponsesWarning'], this.ss.texts()['common_reset'], this.ss.texts()['common_buttonCancel'], 'color-11', 'color-7');
    if (!res) return;
    if (res.confirm === true) {

      this.habitForm.get('description')?.setValue('');
      this.habitForm.get('booleanAnswer')?.setValue(null);
      this.habitForm.get('numericAnswer')?.setValue(null);
      this.habitForm.get('timerAnswer')?.setValue(null);
      this.habitForm.get('singleAnswer')?.setValue(null);
      this.habitForm.get('multipleAnswer')?.setValue(null);
      this.habitForm.get('tags')?.setValue([]);

      this.habitInitial.description = '';
      this.habitInitial.booleanAnswer = null;
      this.habitInitial.numericAnswer = null;
      this.habitInitial.timerAnswer = null;
      this.habitInitial.singleAnswer = null;
      this.habitInitial.multipleAnswer = null;
      this.habitInitial.tags = [];
      this.habitInitial.attachments = {};
      this.numericAnswers.set([]);

      if (this.mode() === 'edit') {
        this.cc.isLoading.set(true);
        const habitData = this.habitValue();
        const currentAttachments: Attachment[] = Object.values(this.habitForm.value.attachments);
        const deletedAttachments: Attachment[] = Object.values(this.deletedAttachments.value);
        const allAttachments = [...currentAttachments, ...deletedAttachments];
        await this.attachmentsService.deleteAttachments(allAttachments, this.basePath);
        this.habitForm.get('attachments')?.reset();
        this.clearAttachments();
        this.habitForm.setControl('attachments', this.fb.group({}));
        const updatedHabit = { ...habitData, ...this.habitForm.value };
        updatedHabit.attachments = {};
        console.log("updatedHabit--->", updatedHabit);
        this.habitStore.updateHabits([updatedHabit]);
        this.cc.isLoading.set(false);
      } else {
        this.clearAttachments();
      }
    }
  }

  isFormHasValue(): boolean {
    const commonCheck = this.habitForm.value.description?.trim() !== '' || this.habitForm.value.attachments.length > 0 || this.habitForm.value.tags.length > 0;
    if (commonCheck) return true;
    switch (this.habitType()) {
      case 'boolean':
        return this.habitForm.value.booleanAnswer !== null;
      case 'numeric':
        return this.habitForm.value.numericAnswer && this.habitForm.value.numericAnswer.length > 0;
      case 'timer':
        return this.habitForm.value.timerAnswer && this.habitForm.value.timerAnswer.length > 0;
      case 'single':
        return this.habitForm.value.singleAnswer !== null;
      case 'multiple':
        return this.habitForm.value.multipleAnswer && this.habitForm.value.multipleAnswer.length > 0;
      default:
        return false;
    }
  }

  addDescription() {
    this.isDescription = true;
    this.scrollToBottom();
  };

  scrollToBottom() {
    setTimeout(() => {
      const el = document.getElementById('habitContentBlock');
      if (el) {
        el.scrollTo({
          top: el.scrollHeight + 30,
          behavior: 'smooth'  // optional: smooth scroll
        });
      }
    }, 100);
  }

  openHashtagsDialog() {
    const dialog = this.modalService.openLevel2(HashtagComponent, {
      values: this.habitForm.value.tags,
      type: 'map'
    }, {
      disableClose: true
    })

    this.modalService.getClosedObservable(dialog).pipe(takeUntil(this.unSubscribe)).subscribe(result => {
      if (result) {
        this.habitForm.get('tags')?.setValue(result);
        this.scrollToBottom();
      }
      this.cdr.markForCheck();
    });
  }

  openComments() {
    const commentsDialog = this.modalService.openLevel2(
      CommentsListComponent,
      {
        entityId: this.habitInitial.id,
        collection: FirestoreCollection.HabitActions,
        members: this.setup().members,
        entityUid: this.setup().uid,
        newEntity: this.mode() === 'new' ? { ...this.habitInitial } : null
      },
      { disableClose: true, autoFocus: false }
    );
  }

  openCompletionAlert() {
    const alertDialog = this.modalService.openLevel3(InputAlertToneSelectComponent, {
      value: this.setup().alarmSoundType,
      volume: this.setup().alarmSoundVolume,
      hideVolume: false,
      titleKey: 'soundSelect_titleTopBar'
    }, {
      disableClose: true,
      autoFocus: false
    });

    this.modalService.getClosedObservable(alertDialog).pipe(takeUntil(this.unSubscribe)).subscribe(async (result) => {
      if (result) {
        const updatedSetup = {
          ...this.setup(),
          alarmSoundType: result.value,
          alarmSoundVolume: result.volume
        };
        this.habitStore.updateSetups([updatedSetup]);
        this.cdr.markForCheck();
      }
    });
  }

  async closeDialog() {
    if (this.hasChanges()) {
      const res = await this.alertService.confirm(this.ss.texts()['saveChanges_title'], this.ss.texts()['common_discardContent'], this.ss.texts()['common_save'], this.ss.texts()['common_discard'], 'color-35', 'color-11');
      if (!res) return;
      if (res.confirm === true) {
        this.save();
      } else if (res.confirm === false) {
        this.close();
      }
    } else {
      this.close();
    }
  }

  private close(result?: any): void {
    if (this.dialogRef) {
      this.dialogRef.close(result);
    } else if (this.bottomSheetRef) {
      this.bottomSheetRef.dismiss(result);
    }
  }

  async deleteHabit() {
    const res = await this.alertService.confirmRevamped({
      title: this.ss.texts()['deleteInvalidEntry_title'],
      description: this.ss.texts()['deleteInvalidEntry_content'],
      yesButtonText: this.ss.texts()['common_delete'],
      noButtonText: this.ss.texts()['common_buttonCancel'],
      yesButtonColor: 'color-11',
      noButtonColor: 'color-7',
    });
    if (!res) return;
    const habit = this.habitValue();
    habit.deletedAt = new Date();
    this.habitStore.updateHabits([habit]);
    this.close();
    this.alertService.toast('invalidEntryDeleted_content', 'common_dismiss');
  }

  async deleteAllInvalidEntries() {
    const res = await this.alertService.confirmRevamped({
      title: this.ss.texts()['deleteAllInvalidEntries_title'],
      description: this.ss.texts()['deleteAllInvalidEntries_habitContent'],
      yesButtonText: this.ss.texts()['deleteAllInvalidEntries_deleteAll'],
      noButtonText: this.ss.texts()['common_buttonCancel'],
      yesButtonColor: 'color-11',
      noButtonColor: 'color-7',
    });
    if (!res) return;
    const allAssociatedEntities = this.habitStore.setupToActions()[this.setup().id];
    const deletedEntities: Habit[] = [];
    allAssociatedEntities.forEach(entity => {
      if (entity.deletedAt) return;
      const dateString = entity.dueAt.dateString;
      const isValidEntry = this.availableDates().includes(dateString);
      if (isValidEntry) return;
      entity.deletedAt = new Date();
      deletedEntities.push(entity);
    });
    if (deletedEntities.length === 0) return;
    this.habitStore.updateHabits(deletedEntities);
    this.close();
    this.alertService.toast('allInvalidEntriesDeleted_content', 'common_dismiss');
  }

  focusEditor() {
    this.textEditor?.focusEditor();
  }

  blurEditor() {
    this.textEditor?.blurEditor();
  }

  onToolbarClose(event: { shouldBlur: boolean }) {
    if (event.shouldBlur) {
      this.blurEditor();
    }
  }

  onDropdownPosition(position: { top: number; left: number }) {
    this.dropdownPosition.set(position);
  }

  editOptions(opt: 'bold' | 'italic' | 'underline' | 'list', value?: 'ordered' | 'bullet') {
    // Apply format
    this.textEditor?.toggleFormat(opt, value);

    // Keep editor focused after formatting (both mobile and desktop)
    setTimeout(() => {
      this.textEditor?.focusEditor();
    }, 50);
  }

  getLastUpdatedByText(): string {
    return this.lastUpdatedByService.getLastUpdatedByString({
      lastUpdatedByUid: this.habitValue().lastUpdatedBy,
      membersModel: this.habitValue().members,
      lastUpdatedAt: this.habitValue().localUpdatedAt,
    });
  }

  /**
   * Execute a menu action dynamically
   * Called from template when user clicks a menu item
   */
  executeAction(action: MenuAction): void {
    const methodName = action.action;
    const self = this as Record<string, any>;
    if (typeof self[methodName] === 'function') {
      self[methodName]();
    } else {
      console.log(
        `HabitFormComponent: Action method '${methodName}' not found`
      );
    }
  }

  /**
   * Get badge value for a menu action
   * Called from template to display badges
   */
  getBadgeValue(action: MenuAction): string | number | null {
    return this.menuManager.getBadgeValue(action, this);
  }

  /**
   * Placeholder for kebab menu trigger
   * Required by menu config but doesn't need implementation
   */
  openKebabMenu(): void {
    // Kebab menu is handled by Angular Material menu trigger
    // This method exists only to satisfy the config action requirement
  }

  ngOnDestroy() {
    this.as.screenExitEvent(this.mapService.habitActionTypeAnalyticsMap()[this.habitType()], this.screenEnterTime(), this.habitTypeString());
    this.unSubscribe?.complete();
    this.unSubscribe?.next();
  }
}
